import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { REVIEW_STATUSES, type Paginated } from '@insurensa/shared';
import { Badge, Card, PageTitle, Stars } from '@insurensa/ui';
import { useFormat, useT } from '@insurensa/i18n';
import { api, qs } from '../lib/api.js';
import { BlockSkeleton, ErrorAlert } from '../components/ui.js';

interface ReviewRow {
  id: string;
  stars: number;
  title: string;
  body: string;
  status: string;
  createdAt: string;
  moderatedAt: string | null;
  moderationNote: string | null;
  partner: { id: string; companyName: string };
  user: { id: string; email: string };
  moderatedBy: { id: string; name: string } | null;
  attachments: Array<{ id: string; url: string; mimeType: string }>;
}

/**
 * Replaces reviews.php, approvedReviews.php, rejectedReviews.php and
 * updateReviewStatus.php — four pages that were one filtered list.
 *
 * Kept as cards rather than a DataTable because the review body is long-form
 * prose; the legacy pages truncated it into a table cell and made moderation
 * decisions harder than they needed to be.
 */
export function ReviewsPage(): JSX.Element {
  const t = useT();
  const fmt = useFormat();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();
  const status = searchParams.get('status') ?? 'PENDING';
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const { data, error, isLoading } = useQuery({
    queryKey: ['reviews', status, page],
    queryFn: () => api.get<Paginated<ReviewRow>>(`/admin/reviews${qs({ status, page, pageSize: 20 })}`),
  });

  const moderate = useMutation({
    mutationFn: (input: { id: string; status: 'APPROVED' | 'REJECTED'; note?: string }) =>
      api.post(`/admin/reviews/${input.id}/moderate`, { status: input.status, note: input.note }),
    onSuccess: () => {
      setNoteFor(null);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['reviews'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-counts'] });
    },
  });

  const setStatus = (value: string): void => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set('status', value);
    else params.delete('status');
    setSearchParams(params);
    setPage(1);
  };

  return (
    <>
      <PageTitle
        heading={t('admin.nav.reviews')}
        subheading={t('admin.reviews.subheading')}
        icon="pe-7s-star"
        actions={
          <select
            className="form-control form-control-sm"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            aria-label={t('admin.reviews.filterLabel')}
          >
            <option value="">{t('common.all')}</option>
            {REVIEW_STATUSES.map((value) => (
              <option key={value} value={value}>
                {fmt.review(value)}
              </option>
            ))}
          </select>
        }
      />

      <ErrorAlert error={error} />
      <ErrorAlert error={moderate.error} />

      {isLoading && <BlockSkeleton height={140} />}

      {data?.items.length === 0 && !isLoading && (
        <Card>
          <div className="text-center text-muted py-4">
            {t('admin.reviews.emptyForStatus')}
          </div>
        </Card>
      )}

      {data?.items.map((review) => (
        <Card key={review.id}>
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <Stars value={review.stars} showValue />
              <strong className="ml-2">{review.title}</strong>
            </div>
            <Badge value={review.status}>{fmt.review(review.status)}</Badge>
          </div>

          <p className="text-muted">{review.body}</p>

          <div className="small text-muted mb-2">
            {review.user.email} · {fmt.dateTime(review.createdAt)} · {t('fields.partner')}:{' '}
            <Link to={`/partners/${review.partner.id}`}>{review.partner.companyName}</Link>
            {review.moderatedBy && (
              <> · {t('admin.reviews.moderatedBy', { name: review.moderatedBy.name })}</>
            )}
          </div>

          {review.attachments.length > 0 && (
            <div className="small mb-2">
              {review.attachments.map((attachment, index) => (
                <a
                  key={attachment.id}
                  href={attachment.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="mr-2"
                >
                  {t('admin.reviews.attachment', { index: index + 1 })}
                </a>
              ))}
            </div>
          )}

          {review.moderationNote && (
            <div className="small text-muted mb-2">
              {t('admin.reviews.notePrefix', { note: review.moderationNote })}
            </div>
          )}

          {noteFor === review.id ? (
            <div className="form-inline">
              <input
                type="text"
                className="form-control form-control-sm mr-2"
                placeholder={t('admin.reviews.reasonPlaceholder')}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                style={{ minWidth: 260 }}
              />
              <button
                type="button"
                className="btn btn-danger btn-sm mr-2"
                disabled={moderate.isPending}
                onClick={() =>
                  moderate.mutate({
                    id: review.id,
                    status: 'REJECTED',
                    note: note.trim() || undefined,
                  })
                }
              >
                {t('common.decline')}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setNoteFor(null);
                  setNote('');
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <>
              {review.status !== 'APPROVED' && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm mr-2"
                  disabled={moderate.isPending}
                  onClick={() => moderate.mutate({ id: review.id, status: 'APPROVED' })}
                >
                  {t('common.approve')}
                </button>
              )}
              {review.status !== 'REJECTED' && (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => {
                    setNoteFor(review.id);
                    setNote('');
                  }}
                >
                  {t('common.decline')}
                </button>
              )}
            </>
          )}
        </Card>
      ))}

      {data && data.totalPages > 1 && (
        <nav>
          <ul className="pagination">
            <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
              <button type="button" className="page-link" onClick={() => setPage(page - 1)}>
                {t('table.previous')}
              </button>
            </li>
            <li className="page-item disabled">
              <span className="page-link">
                {t('table.pageOf', { page: data.page, total: data.totalPages })}
              </span>
            </li>
            <li className={`page-item ${page >= data.totalPages ? 'disabled' : ''}`}>
              <button type="button" className="page-link" onClick={() => setPage(page + 1)}>
                {t('table.next')}
              </button>
            </li>
          </ul>
        </nav>
      )}
    </>
  );
}
